import React, { useState } from 'react';
import config from 'config';
import {
    Grid,
    TextField,
    Button,
    Select,
    MenuItem,
    CircularProgress
  } from '@mui/material';
import apiService from 'services/apiService';
import { useSelector, useDispatch } from 'react-redux';
import { translateKeyword, translation } from 'utils/translate';
import { isMobile } from 'react-device-detect';
import { styled } from '@mui/material/styles';
import texts from 'texts'
import {
    reportError,
} from 'actions/errorActions';
import Tip from 'components/Tip';
import './Hakuvahti.css';

const operations = ['basic', /*'lausunto',*/ 'confirm', 'cancel'];
const StyledButton = styled(Button)({
  backgroundColor: 'var(--primary)',
  borderRadius: 5,
  border: 0,
  height: '48px',
  textTransform: 'none',
  color: 'white',
  fontSize: '16px',
  lineHeight: '18px',
  '&:hover': {
    backgroundColor: 'var(--primary-dark)',
  },
});

const WhiteButton = styled(Button)({
  backgroundColor: 'white',
  borderRadius: 5,
  border: '3px solid var(--primary)',
  height: '48px',
  marginLeft: 10,
  textTransform: 'none',
  color: 'var(--primary)',
  fontSize: '14px',
  lineHeight: '18px',
  '&:hover': {
    backgroundColor: 'var(--blue-verylight)',
  },
});

const WarningBox = styled(Button)({
  height: 56,
  backgroundColor: '#ffed65',
  textTransform: 'none',
  color: '#988600',
  fontSize: '14px',
  lineHeight: '18px',
  '&:hover': {
    backgroundColor: '#ffed65',
  },
});

const SuccessBox = styled(Button)({
  height: 56,
  backgroundColor: '#ace7ca',
  textTransform: 'none',
  color: '#009c50',
  fontSize: '14px',
  lineHeight: '18px',
  '&:hover': {
    backgroundColor: '#ace7ca',
  },
});

const Hakuvahti = () => {
    const lang = useSelector((store) => store.lang)
    const [content, setContent] = useState({
        op: 'basic', term:'', docType:'all', mail:'', code:''});
    const [status, setStatus] = useState('');
    const [disableButtons, setDisableButtons] = useState(false);
    const dispatch = useDispatch();

    const changeOp = async (event) => {
        handleStatus('')
        setContent({...content, op: event})
    }

    const handleStatus = (event) => {
        setStatus(event)
    }

    const isContentValid = (content) => {
      // if operation is confirm or cancel, code must be present
      if (content.op === 'confirm' || content.op === 'cancel') {
        if (content.code === '') return false;
      }
      // return true if content.mail matches the email regex xx@xx.xx
      const emailRe = /\S+@\S+\.\S+/;
      return emailRe.test(content.mail);
    }

    const handleRequest = async () => {
        setDisableButtons(true)
        handleStatus('processing')
        // check for problems with essential fields
        if (!isContentValid(content)) {
          handleStatus('warning')
        } else {
            try {
                const response = await apiService.sendHakuvahtiRequest(
                    {...content, lang:lang}
                );
                if (response.status === 200) {
                    setStatus('success')
                    emptyForm()
                } else {
                    setStatus('warning')
                }
            } catch (err) {
                dispatch(reportError(err));
            }
        }
        setDisableButtons(false)
    }

    const emptyForm = () => {
        setContent({...content, term:'', docType:'all', mail:'', code:''})
    }

    return (
        <div>
            <div className="intro-container">
                <div className={isMobile ? "page-wrapper-mobile" : "page-wrapper"}>
                    <h1 className="intro-title">
                        {translation("hakuvahti", "title", lang)}
                    </h1><p className='intro-text'>
                        {translation("hakuvahti", "about_1", lang)}
                    </p><p className='intro-text'>
                        {translation("hakuvahti", "about_2", lang)}
                    </p><p className='intro-text'>
                        {translation("hakuvahti", "about_3", lang)}
                    </p><h2 className='intro-subtitle'>
                        {translation("hakuvahti", "select_function", lang)}
                    </h2>
                </div>
            </div>
            <div className="op-type-select-area">
                {operations.map((key) => (
                    <Tip title={translation("hakuvahti", key, "tooltip", lang)} key={key}>
                        <button
                            type="button"
                            className={
                                content.op === key
                                    ? 'op-type-select op-type-select--active'
                                    : 'op-type-select'
                                }
                            onClick={() => changeOp(key)}
                        >
                            {translation("hakuvahti", key, "name", lang)}
                        </button>
                    </Tip>
                ))}
            </div>

            <div className='form-container'>
                {content.op === 'basic' ? (
                    <Grid item xs={12} sm={12} md={8}>
                        <h4 className='proximity-doctype-h4'>
                            {translateKeyword("type", lang)}
                            <Tip title={translation("asiakirjatyyppi_tooltip", lang)}>
                                <span alt={translation("asiakirjatyyppi_tooltip", lang)} className="tooltip-help tippable"></span>
                            </Tip>
                        </h4>
                        <Select
                            className="doctype-select"
                            value={content.docType || "all"}
                            onChange={(e) => setContent({...content, docType: e.target.value})}
                        >
                        {Object.entries(config.docGroups).map(
                            ([key, value]) =>
                                value.searchable && (
                                    <MenuItem value={key} key={key}>
                                        {translation(value.name, lang)}
                                    </MenuItem>
                                ),
                            )}
                        </Select>
                        <h4 className='proximity-doctype-h4'>
                            {translation("hakuvahti", "filter_info", lang)}
                            <Tip title={translation("texts_tooltip_basic", lang)}>
                                <span alt={texts.info.help} className="tooltip-help tippable"></span>
                            </Tip>
                        </h4>
                        <TextField
                            className="basic-search-term"
                            onChange={(e) => setContent({...content, term : e.target.value})}
                            key={'rajaa'}
                            label={translation("hakuvahti", "filter_labels", content.docType, lang)}
                            variant="outlined"
                            value={content.term? content.term : ''}
                        />
                        <div>
                            <h4 className='proximity-doctype-h4'>
                                {translation("hakuvahti", "email", lang)}            
                            </h4>
                            <TextField
                                className='basic-text-field'
                                onChange={(e) => setContent({...content, mail: e.target.value})}
                                label={translation("hakuvahti", "email", lang)}
                                variant="outlined"
                                value={content.mail? content.mail : ''}
                            />
                        </div>
                    </Grid>
                /*
                -- lausuntovahti --
                ) : content.op === 'lausunto' ? (
                    <Grid item xs={12} sm={12} md={8}>
                        <h4 className='proximity-doctype-h4'>
                            {translation("hakuvahti", "filter_info", lang)}
                            <Tip title={translation("texts_tooltip_basic", lang)}>
                                <span alt={texts.info.help} className="tooltip-help tippable"></span>
                            </Tip>
                        </h4>
                        <TextField
                            className="basic-search-term"
                            onChange={(e) => setContent({...content, term: e.target.value})}
                            label={translation("hakuvahti", "filter_labels", 'lausunto', lang)}
                            variant="outlined"
                            value={content.term? content.term : ''}
                        />
                        <h4 className='proximity-doctype-h4'>
                            {translation("hakuvahti", "email", lang)}            
                        </h4>
                        <TextField
                            className='basic-text-field'
                            onChange={(e) => setContent({...content, mail:e.target.value})}
                            label={translation("hakuvahti", "email", lang)}
                            variant="outlined"
                            value={content.mail? content.mail : ''}
                        />
                    </Grid>
                */
                ) : content.op === 'confirm' ? (
                    <Grid item xs={12} sm={12} md={8}>
                        <h4 className='proximity-doctype-h4'>
                            {translation("hakuvahti", "email", lang)}            
                        </h4>
                        <TextField
                            className='basic-text-field'
                            onChange={(e) => setContent({...content, mail: e.target.value})}
                            label={translation("hakuvahti", "email", lang)}
                            variant="outlined"
                            value={content.mail? content.mail : ''}
                        />
                        <h4 className='proximity-doctype-h4'>
                            {translation("hakuvahti", "code", "name", lang)}
                            <Tip title={translation("hakuvahti", "code", "tooltip", lang)}>
                                <span alt={translation("hakuvahti", "code", "tooltip", lang)} className="tooltip-help tippable"></span>
                            </Tip>
                        </h4>
                        <TextField
                            className='basic-text-field'
                            onChange={(e) => setContent({...content, code: e.target.value})}
                            label={translation("hakuvahti", "code", "name", lang)}
                            variant="outlined"
                            value={content.code? content.code : ''}
                        />
                    </Grid>
                ) : content.op === 'cancel' ? (
                    <Grid item xs={12} sm={12} md={8}>
                        <h4 className='proximity-doctype-h4'>
                            {translation("hakuvahti", "email", lang)}            
                        </h4>
                        <TextField
                            className='basic-text-field'
                            onChange={(e) => setContent({...content, mail: e.target.value})}
                            label={translation("hakuvahti", "email", lang)}
                            variant="outlined"
                            value={content.mail? content.mail : ''}
                        />
                        <h4 className='proximity-doctype-h4'>
                            {translation("hakuvahti", "code", "name", lang)}
                            <Tip title={translation("hakuvahti", "code", "tooltip", lang)}>
                                <span alt={translation("hakuvahti", "code", "tooltip", lang)} className="tooltip-help tippable"></span>
                            </Tip>
                        </h4>
                        <TextField
                            className='basic-text-field'
                            onChange={(e) => setContent({...content, code: e.target.value})}
                            label={translation("hakuvahti", "code", "name", lang)}
                            variant="outlined"
                            value={content.code? content.code : ''}
                        />
                    </Grid>
                ) : <div>Hakuvahti poissa käytöstä hetkellisesti. Pahoittelut häiriöstä.</div>}
                
                <div className='button-container'>
                    <StyledButton
                        disabled={disableButtons}
                        onClick={handleRequest}
                        variant="contained"
                    >
                        {translation("hakuvahti", content.op, "request_button", lang)}
                    </StyledButton>
                    <WhiteButton
                        disabled={disableButtons}
                        onClick={emptyForm}
                        variant="contained"
                    >
                        {translation("hakuvahti", "emptyformbutton", lang)}
                    </WhiteButton>
                </div>
                
                {status === 'warning' ? (
                    <WarningBox className='notifbox'
                        disableTouchRipple
                        disableElevation
                        onClick={status !== 'processing' ? () => handleStatus(''):undefined}
                        variant="contained"
                    >
                        {translation("hakuvahti", "warning_notif", lang)}
                    </WarningBox>
                ) : status === 'success' ? (
                    <SuccessBox className='notifbox'
                        disableTouchRipple
                        disableElevation
                        onClick={status !== 'processing' ? () => handleStatus(''):undefined}
                        variant="contained"
                    >
                        {translation("hakuvahti", "success_notif", lang)}
                    </SuccessBox>
                ) : status === 'processing' ? (
                    <CircularProgress></CircularProgress>
                ) : (<></>)}
            </div>
        </div>
    );
};

export default Hakuvahti;
